import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PencilSimple, Check, X, Info, CurrencyDollar, Clock, Question, CalendarPlus, Briefcase, Handshake } from '@phosphor-icons/react';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FIELDS = [
  { key: 'reason_for_change', label: 'Reason for Change', icon: Question, placeholder: 'e.g. Career growth' },
  { key: 'current_salary', label: 'Current Salary', icon: CurrencyDollar, placeholder: 'e.g. 12 LPA', isSalary: true },
  { key: 'expected_salary', label: 'Expected Salary', icon: CurrencyDollar, placeholder: 'e.g. 18 LPA', isSalary: true },
  { key: 'notice_period', label: 'Notice Period', icon: Clock, placeholder: 'e.g. 30 days' },
];

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD', 'AUD', 'CAD'];
const CURRENCY_SYMBOLS = { INR: '₹', USD: '$', EUR: '€', GBP: '£', AED: 'AED', SGD: 'S$', AUD: 'A$', CAD: 'C$' };

const AdditionalDetails = ({ candidate, onUpdate }) => {
  const { hasPage } = useAuth();
  const canManage = hasPage('additional-details');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [engagementTypes, setEngagementTypes] = useState([]);
  const [partnerNames, setPartnerNames] = useState([]);
  const [masterDataLoading, setMasterDataLoading] = useState(true);

  useEffect(() => {
    setForm({
      reason_for_change: candidate.reason_for_change || '',
      current_salary: candidate.current_salary || '',
      expected_salary: candidate.expected_salary || '',
      salary_currency: candidate.salary_currency || 'INR',
      notice_period: candidate.notice_period || '',
      engagement_type: candidate.engagement_type || '',
      source: candidate.source || '',
      partner_name: candidate.partner_name || '',
    });
  }, [candidate]);

  useEffect(() => {
    axios.get(`${API}/master-data/values`)
      .then(({ data }) => {
        setEngagementTypes(data.engagement_type || []);
        setPartnerNames(data.partner_name || []);
      })
      .catch((err) => console.error('Error fetching master data values:', err))
      .finally(() => setMasterDataLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/candidates/${candidate.id}`, form);
      toast.success('Additional details saved');
      setEditing(false);
      onUpdate?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setForm({
      reason_for_change: candidate.reason_for_change || '',
      current_salary: candidate.current_salary || '',
      expected_salary: candidate.expected_salary || '',
      salary_currency: candidate.salary_currency || 'INR',
      notice_period: candidate.notice_period || '',
      engagement_type: candidate.engagement_type || '',
      source: candidate.source || '',
      partner_name: candidate.partner_name || '',
    });
    setEditing(false);
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return iso; }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6" data-testid="additional-details-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Info size={22} weight="duotone" className="text-[#002FA7]" />
          <h2 className="text-xl font-medium text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Additional Details
          </h2>
        </div>
        {canManage && (
          editing ? (
            <div className="flex items-center space-x-2">
              <button onClick={save} disabled={saving} className="btn-primary flex items-center space-x-1 px-3 py-1.5 rounded text-sm font-medium disabled:opacity-50" data-testid="details-save">
                <Check size={14} weight="bold" />
                <span>{saving ? 'Saving…' : 'Save'}</span>
              </button>
              <button onClick={cancel} disabled={saving} className="flex items-center space-x-1 px-3 py-1.5 border border-slate-300 rounded text-sm text-slate-600 hover:bg-slate-50" data-testid="details-cancel">
                <X size={14} />
                <span>Cancel</span>
              </button>
            </div>
          ) : (
            <button onClick={() => setEditing(true)} className="flex items-center space-x-1 px-3 py-1.5 text-sm text-slate-600 hover:text-[#002FA7] hover:bg-slate-50 rounded transition-colors" data-testid="details-edit">
              <PencilSimple size={14} />
              <span>Edit</span>
            </button>
          )
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Date of Upload - read only */}
        <div className="flex items-start space-x-3">
          <div className="bg-blue-50 text-[#002FA7] p-2 rounded-md flex-shrink-0">
            <CalendarPlus size={18} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Date of Upload</p>
            <p className="text-sm text-slate-800" data-testid="date-of-upload">
              {formatDate(candidate.created_at)}
            </p>
          </div>
        </div>

        {FIELDS.map(({ key, label, icon: Icon, placeholder, isSalary }) => (
          <div key={key} className="flex items-start space-x-3">
            <div className="bg-blue-50 text-[#002FA7] p-2 rounded-md flex-shrink-0">
              <Icon size={18} weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">{label}</p>
              {editing ? (
                key === 'reason_for_change' ? (
                  <textarea
                    value={form[key]}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value.slice(0, 2000) })}
                    placeholder={placeholder}
                    rows={2}
                    className="w-full px-2 py-1 bg-white border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                    data-testid={`details-input-${key}`}
                  />
                ) : isSalary ? (
                  <div className="flex items-center space-x-1.5">
                    <select
                      value={form.salary_currency || 'INR'}
                      onChange={(e) => setForm({ ...form, salary_currency: e.target.value })}
                      className="px-1.5 py-1 bg-white border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                      data-testid={`details-input-${key}-currency`}
                    >
                      {CURRENCIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={form[key]}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value.slice(0, 100) })}
                      placeholder={placeholder}
                      className="flex-1 min-w-0 px-2 py-1 bg-white border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                      data-testid={`details-input-${key}`}
                    />
                  </div>
                ) : (
                  <input
                    type="text"
                    value={form[key]}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value.slice(0, 100) })}
                    placeholder={placeholder}
                    className="w-full px-2 py-1 bg-white border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                    data-testid={`details-input-${key}`}
                  />
                )
              ) : (
                <p className={`text-sm ${candidate[key] ? 'text-slate-800' : 'text-slate-400 italic'} whitespace-pre-wrap`} data-testid={`details-value-${key}`}>
                  {isSalary && !canManage
                    ? 'xxxx'
                    : isSalary && candidate[key]
                    ? `${CURRENCY_SYMBOLS[candidate.salary_currency] || candidate.salary_currency || ''} ${candidate[key]}`.trim()
                    : (candidate[key] || '—')}
                </p>
              )}
            </div>
          </div>
        ))}

        {/* Engagement Type - dropdown */}
        <div className="flex items-start space-x-3">
          <div className="bg-blue-50 text-[#002FA7] p-2 rounded-md flex-shrink-0">
            <Briefcase size={18} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Engagement Type</p>
            {editing ? (
              <select
                value={form.engagement_type || ''}
                onChange={(e) => setForm({ ...form, engagement_type: e.target.value })}
                className="w-full px-2 py-1 bg-white border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                data-testid="details-input-engagement_type"
              >
                <option value="">—</option>
                {engagementTypes.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            ) : (
              <p className={`text-sm ${candidate.engagement_type ? 'text-slate-800' : 'text-slate-400 italic'}`} data-testid="details-value-engagement_type">
                {candidate.engagement_type || '—'}
              </p>
            )}
          </div>
        </div>

        {/* Source - Internal / Partner */}
        <div className="flex items-start space-x-3">
          <div className="bg-blue-50 text-[#002FA7] p-2 rounded-md flex-shrink-0">
            <Handshake size={18} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Source</p>
            {editing ? (
              <div className="space-y-2">
                <select
                  value={form.source || ''}
                  onChange={(e) => setForm({ ...form, source: e.target.value, partner_name: e.target.value === 'Partner' ? form.partner_name : '' })}
                  className="w-full px-2 py-1 bg-white border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                  data-testid="details-input-source"
                >
                  <option value="">—</option>
                  <option value="Internal">Internal</option>
                  <option value="Partner">Partner</option>
                </select>
                {form.source === 'Partner' && (
                  <select
                    value={form.partner_name || ''}
                    onChange={(e) => setForm({ ...form, partner_name: e.target.value })}
                    disabled={masterDataLoading}
                    className="w-full px-2 py-1 bg-white border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] disabled:bg-slate-50 disabled:text-slate-400"
                    data-testid="details-input-partner_name"
                  >
                    {masterDataLoading ? (
                      <option value="">Please wait, loading partners…</option>
                    ) : (
                      <>
                        <option value="">Select partner</option>
                        {partnerNames.map((v) => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </>
                    )}
                  </select>
                )}
              </div>
            ) : (
              <p className={`text-sm ${candidate.source ? 'text-slate-800' : 'text-slate-400 italic'}`} data-testid="details-value-source">
                {candidate.source ? (candidate.source === 'Partner' && candidate.partner_name ? `Partner — ${candidate.partner_name}` : candidate.source) : '—'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdditionalDetails;
